import { useState, useRef } from 'react'
import { motion, useInView } from 'framer-motion'
import { SPRING, UP } from '@/lib/motion'
import { Phone, MapPin, Clock } from 'lucide-react'

export function Contact() {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: true, margin: '-80px' })
  const [formState, setFormState] = useState<'idle' | 'submitting' | 'success'>('idle')
  const [formData, setFormData] = useState({ name: '', phone: '', message: '' })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setFormState('submitting')
    // Simulate submission
    setTimeout(() => setFormState('success'), 1500)
  }

  return (
    <section
      ref={ref}
      style={{
        background: 'var(--elevated)',
        padding: 'var(--pad-section) max(5vw, 24px)'
      }}
    >
      <div style={{ maxWidth: 1400, margin: '0 auto' }}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 80,
            alignItems: 'start'
          }}
        >
          {/* LEFT: Form */}
          <motion.div variants={UP} initial="hidden" animate={isInView ? 'show' : 'hidden'}>
            <span
              style={{
                fontFamily: 'DM Sans',
                fontSize: '0.8rem',
                color: 'var(--lime)',
                letterSpacing: '0.15em',
                textTransform: 'uppercase'
              }}
            >
              Get In Touch · ያግኙን
            </span>
            <h2
              style={{
                fontFamily: 'Bebas Neue',
                fontSize: 'var(--h2)',
                color: 'var(--white)',
                marginTop: 12,
                marginBottom: 32
              }}
            >
              Contact Us
            </h2>

            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
              <div>
                <label
                  style={{
                    display: 'block',
                    fontFamily: 'DM Sans',
                    fontSize: '0.85rem',
                    color: 'var(--white-60)',
                    marginBottom: 8
                  }}
                >
                  Your Name · ስምዎ
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  style={{
                    width: '100%',
                    padding: '14px 18px',
                    background: 'var(--surface)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    borderRadius: 12,
                    color: 'var(--white)',
                    fontFamily: 'DM Sans',
                    fontSize: '1rem',
                    outline: 'none'
                  }}
                />
              </div>

              <div>
                <label
                  style={{
                    display: 'block',
                    fontFamily: 'DM Sans',
                    fontSize: '0.85rem',
                    color: 'var(--white-60)',
                    marginBottom: 8
                  }}
                >
                  Phone Number · ስልክ
                </label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  required
                  style={{
                    width: '100%',
                    padding: '14px 18px',
                    background: 'var(--surface)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    borderRadius: 12,
                    color: 'var(--white)',
                    fontFamily: 'DM Sans',
                    fontSize: '1rem',
                    outline: 'none'
                  }}
                />
              </div>

              <div>
                <label
                  style={{
                    display: 'block',
                    fontFamily: 'DM Sans',
                    fontSize: '0.85rem',
                    color: 'var(--white-60)',
                    marginBottom: 8
                  }}
                >
                  Message · መልእክት
                </label>
                <textarea
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  rows={4}
                  style={{
                    width: '100%',
                    padding: '14px 18px',
                    background: 'var(--surface)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    borderRadius: 12,
                    color: 'var(--white)',
                    fontFamily: 'DM Sans',
                    fontSize: '1rem',
                    outline: 'none',
                    resize: 'vertical'
                  }}
                />
              </div>

              <motion.button
                type="submit"
                data-cursor="hover"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                disabled={formState !== 'idle'}
                style={{
                  padding: '16px 32px',
                  background: formState === 'success' ? 'var(--lime)' : 'var(--surface)',
                  border: '1px solid var(--lime)',
                  borderRadius: 12,
                  color: formState === 'success' ? 'var(--black)' : 'var(--lime)',
                  fontFamily: 'DM Sans',
                  fontWeight: 700,
                  fontSize: '1rem',
                  cursor: formState === 'idle' ? 'pointer' : 'not-allowed',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8
                }}
              >
                {formState === 'idle' && 'Send Message →'}
                {formState === 'submitting' && 'Sending...'}
                {formState === 'success' && '✓ Sent!'}
              </motion.button>
            </form>
          </motion.div>

          {/* RIGHT: Contact Info */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ delay: 0.2, ...SPRING }}
          >
            {/* Phone Number */}
            <a
              href="tel:+251997745317"
              style={{
                display: 'block',
                textDecoration: 'none',
                marginBottom: 48
              }}
            >
              <motion.div
                animate={{
                  boxShadow: [
                    '0 0 0px rgba(232,255,0,0)',
                    '0 0 40px rgba(232,255,0,0.3)',
                    '0 0 0px rgba(232,255,0,0)'
                  ]
                }}
                transition={{ duration: 3, repeat: Infinity }}
                style={{
                  padding: 32,
                  background: 'var(--surface)',
                  border: '1px solid rgba(232,255,0,0.2)',
                  borderRadius: 20,
                  textAlign: 'center'
                }}
              >
                <Phone size={32} style={{ color: 'var(--lime)', marginBottom: 16 }} />
                <div
                  style={{
                    fontFamily: 'Bebas Neue',
                    fontSize: '3.5rem',
                    color: 'var(--lime)',
                    lineHeight: 1
                  }}
                >
                  +251 99 774 5317
                </div>
                <div
                  style={{
                    fontFamily: 'DM Sans',
                    fontSize: '0.9rem',
                    color: 'var(--white-60)',
                    marginTop: 8
                  }}
                >
                  Call Now · አሁን ይደውሉ
                </div>
              </motion.div>
            </a>

            {/* Info Cards */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div
                style={{
                  padding: 20,
                  background: 'var(--surface)',
                  borderRadius: 12,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 16
                }}
              >
                <MapPin size={24} style={{ color: 'var(--lime)' }} />
                <div>
                  <div style={{ fontFamily: 'DM Sans', fontWeight: 600, color: 'var(--white)' }}>
                    Addis Ababa, Ethiopia
                  </div>
                  <div style={{ fontFamily: 'DM Sans', fontSize: '0.85rem', color: 'var(--white-60)' }}>
                    Serving all neighborhoods
                  </div>
                </div>
              </div>

              <div
                style={{
                  padding: 20,
                  background: 'var(--surface)',
                  borderRadius: 12,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 16
                }}
              >
                <Clock size={24} style={{ color: 'var(--lime)' }} />
                <div>
                  <div style={{ fontFamily: 'DM Sans', fontWeight: 600, color: 'var(--white)' }}>
                    24/7 Available
                  </div>
                  <div style={{ fontFamily: 'DM Sans', fontSize: '0.85rem', color: 'var(--white-60)' }}>
                    Same-day service available
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  )
}
